import { useId } from "react";
import {
  Area,
  AreaChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type ForecastPoint = {
  year: string;
  base: number;
  savings: number;
};

interface FiveYearForecastCardProps {
  data: ForecastPoint[];
  baseStroke?: string;
  savingsStroke?: string;
  tickColor?: string;
  mutedTickColor?: string;
  isShowTags?: boolean;
  emptyState?: React.ReactNode;
}

export function FiveYearForecastCard({
  data,
  baseStroke = "#3265AA",
  savingsStroke = "#AD5E9D",
  tickColor = "#4c4c4d",
  mutedTickColor = "#9e97a3",
  isShowTags = true,
  emptyState,
}: FiveYearForecastCardProps) {
  const forecastMax = data.length > 0 ? Math.max(...data.map((d) => d.base)) : 0;
  const forecastMin = data.length > 0 ? Math.min(...data.map((d) => d.savings)) : 0;
  const gradientId = useId().replace(/:/g, "");

  return (
    <div className="cma-card">
      <div className="cma-card-header">
        <div>
          <div className="cma-card-title">5-Year Cost Forecast (Inflation-Adjusted)</div>
          <div className="cma-card-subtitle">2025-2030 cost projection with savings offset ($M)</div>
        </div>
        {isShowTags && <span className="cma-card-badge cma-card-badge-purple">FORECAST</span>}
      </div>

      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={baseStroke} stopOpacity={0.18} />
                <stop offset="95%" stopColor={baseStroke} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: tickColor }} />
            <YAxis domain={[Math.round(forecastMin * 0.97), Math.round(forecastMax * 1.03)]} tick={{ fontSize: 11, fill: mutedTickColor }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
            <Area type="monotone" dataKey="base" stroke={baseStroke} strokeWidth={2} fill={`url(#${gradientId})`} name="Base Forecast" />
            <Line type="monotone" dataKey="savings" stroke={savingsStroke} strokeWidth={2} strokeDasharray="5 3" dot={{ fill: savingsStroke, r: 4 }} name="With Savings ($M)" />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        emptyState ?? <div className="cma-no-records">No Records Found</div>
      )}
    </div>
  );
}
